import { useQuery } from '@apollo/client';
import { useNotifications } from '@risksmart-app/components/src/notifications/useNotifications';
import type { Test_Result_Bool_Exp } from '@risksmart-app/web-graphql-client/generated/graphql';
import {
  GetTestResultsDocument,
  Parent_Type_Enum,
} from '@risksmart-app/web-graphql-client/generated/graphql';
import useEntityWhereFilter from 'src/hooks/useEntityWhereFilter';

import { useIsFeatureVisibleToOrg } from '@/utils/featureFlags';

import { useGetTestResults as useGetTestResultsTRPC } from './useGetTestResultsTRPC';

export const useGetTestResults = (skip?: boolean) => {
  const trpcEnabled = useIsFeatureVisibleToOrg('trpc');
  const { addNotification } = useNotifications();

  const where = useEntityWhereFilter<Test_Result_Bool_Exp>(
    Parent_Type_Enum.TestResult,
    {
      RatingType: { _in: ['assessment', 'rating'] },
    }
  );

  const {
    data: graphqlData,
    loading: graphqlLoading,
    refetch: graphqlRefetch,
    error: graphqlError,
  } = useQuery(GetTestResultsDocument, {
    variables: {
      where,
    },
    fetchPolicy: 'no-cache',
    skip: trpcEnabled || skip, // Skip GraphQL query when TRPC is enabled
    onError: (error) => {
      if (!trpcEnabled) {
        addNotification({
          type: 'error',
          content: <>{error.message}</>,
        });
      }
    },
  });

  const {
    data: trpcData,
    loading: trpcLoading,
    refetch: trpcRefetch,
    error: trpcError,
  } = useGetTestResultsTRPC(skip);

  // Return appropriate data based on feature flag
  if (trpcEnabled) {
    return {
      loading: trpcLoading,
      data: trpcData,
      refetch: () => trpcRefetch(),
      error: trpcError,
    };
  }

  return {
    loading: graphqlLoading,
    data: graphqlData,
    refetch: () => graphqlRefetch(),
    error: graphqlError,
  };
};
